import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Car, Warehouse, PersonSimpleBike, GasPump } from "@phosphor-icons/react";
import { Progress } from "../components/ui/progress";

const BANNER = "https://images.pexels.com/photos/94272/sports-car-pkw-auto-vehicle-94272.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CAT_IMAGE = {
  sports: "https://images.pexels.com/photos/210019/pexels-photo-210019.jpeg?auto=compress&cs=tinysrgb&h=350&w=600",
  super: "https://images.pexels.com/photos/1149137/pexels-photo-1149137.jpeg?auto=compress&cs=tinysrgb&h=350&w=600",
  exotics: "https://images.pexels.com/photos/337909/pexels-photo-337909.jpeg?auto=compress&cs=tinysrgb&h=350&w=600",
  muscle: "https://images.pexels.com/photos/1592384/pexels-photo-1592384.jpeg?auto=compress&cs=tinysrgb&h=350&w=600",
  sedan: "https://images.pexels.com/photos/170811/pexels-photo-170811.jpeg?auto=compress&cs=tinysrgb&h=350&w=600",
  coupe: "https://images.pexels.com/photos/136872/pexels-photo-136872.jpeg?auto=compress&cs=tinysrgb&h=350&w=600",
  coupes: "https://images.pexels.com/photos/136872/pexels-photo-136872.jpeg?auto=compress&cs=tinysrgb&h=350&w=600",
  suv: "https://images.pexels.com/photos/2127733/pexels-photo-2127733.jpeg?auto=compress&cs=tinysrgb&h=350&w=600",
  suvs: "https://images.pexels.com/photos/2127733/pexels-photo-2127733.jpeg?auto=compress&cs=tinysrgb&h=350&w=600",
  compact: "https://images.pexels.com/photos/244206/pexels-photo-244206.jpeg?auto=compress&cs=tinysrgb&h=350&w=600",
  compacts: "https://images.pexels.com/photos/244206/pexels-photo-244206.jpeg?auto=compress&cs=tinysrgb&h=350&w=600",
  offroad: "https://images.pexels.com/photos/1119796/pexels-photo-1119796.jpeg?auto=compress&cs=tinysrgb&h=350&w=600",
  motorcycle: "https://images.pexels.com/photos/163210/motorcycles-race-helmets-pilots-163210.jpeg?auto=compress&cs=tinysrgb&h=350&w=600",
  motorcycles: "https://images.pexels.com/photos/163210/motorcycles-race-helmets-pilots-163210.jpeg?auto=compress&cs=tinysrgb&h=350&w=600",
  industrial: "https://images.pexels.com/photos/93398/pexels-photo-93398.jpeg?auto=compress&cs=tinysrgb&h=350&w=600",
  commercial: "https://images.pexels.com/photos/93398/pexels-photo-93398.jpeg?auto=compress&cs=tinysrgb&h=350&w=600",
  emergency: "https://images.pexels.com/photos/163016/crime-scene-blue-lights-police-163016.jpeg?auto=compress&cs=tinysrgb&h=350&w=600",
  van: "https://images.pexels.com/photos/1770310/pexels-photo-1770310.jpeg?auto=compress&cs=tinysrgb&h=350&w=600",
  vans: "https://images.pexels.com/photos/1770310/pexels-photo-1770310.jpeg?auto=compress&cs=tinysrgb&h=350&w=600",
  sportsclassics: "https://images.pexels.com/photos/544542/pexels-photo-544542.jpeg?auto=compress&cs=tinysrgb&h=350&w=600",
  classic: "https://images.pexels.com/photos/544542/pexels-photo-544542.jpeg?auto=compress&cs=tinysrgb&h=350&w=600",
  car: "https://images.pexels.com/photos/210019/pexels-photo-210019.jpeg?auto=compress&cs=tinysrgb&h=350&w=600",
};
const catImage = (c) => CAT_IMAGE[(c || "").toLowerCase()] || CAT_IMAGE.car;

// GTA doesn't have a stable public image CDN — leave empty per requirement.
// Category gives us a subtle color-tinted background so cards still look sharp.
const CAT_ACCENT = {
  sports: "from-blue-900/40 to-black",
  super: "from-red-900/40 to-black",
  exotics: "from-purple-900/40 to-black",
  muscle: "from-orange-900/40 to-black",
  sedan: "from-slate-800/60 to-black",
  coupes: "from-teal-900/40 to-black",
  coupe: "from-teal-900/40 to-black",
  suv: "from-emerald-900/40 to-black",
  suvs: "from-emerald-900/40 to-black",
  compact: "from-gray-800/60 to-black",
  compacts: "from-gray-800/60 to-black",
  offroad: "from-yellow-900/40 to-black",
  motorcycle: "from-pink-900/40 to-black",
  motorcycles: "from-pink-900/40 to-black",
  industrial: "from-amber-900/40 to-black",
  commercial: "from-amber-900/40 to-black",
  emergency: "from-red-800/50 to-black",
  van: "from-cyan-900/40 to-black",
  vans: "from-cyan-900/40 to-black",
  sportsclassics: "from-yellow-800/40 to-black",
  classic: "from-yellow-800/40 to-black",
};
const catAccent = (c) => CAT_ACCENT[(c || "").toLowerCase()] || "from-slate-800/50 to-black";

// Fivemanage R2 bucket with 801 pre-rendered vehicle images.
// If a model doesn't exist there, onError hides the img element and the
// category gradient shows instead.
const vehicleImage = (model) => {
  if (!model) return null;
  const slug = String(model).toLowerCase().trim();
  if (!slug || slug === "unknown" || /^-?\d+$/.test(slug)) return null;
  return `https://r2.fivemanage.com/ZxjPW3RnVbLIpvG0V3NfA/vehicle-studio/${slug}.png`;
};

const catIcon = (c) => c === "motorcycle" || c === "motorcycles" ? PersonSimpleBike : (c === "industrial" || c === "commercial") ? Warehouse : Car;

export default function Vehicles() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/character/vehicles").then(r => setItems(r.data)).catch(() => toast.error("Fahrzeuge konnten nicht geladen werden"));
  }, []);

  const cats = ["all", ...Array.from(new Set(items.map(i => i.category)))];
  const filtered = filter === "all" ? items : items.filter(i => i.category === filter);
  const stored = items.filter(i => i.stored).length;
  const avgCondition = items.length ? Math.round(items.reduce((a, b) => a + (b.condition || 0), 0) / items.length) : 0;

  return (
    <div>
      <div className="relative border-b border-tactical-border">
        <div className="absolute inset-0">
          <img src={BANNER} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/60 to-transparent" />
        </div>
        <div className="relative z-10 px-8 lg:px-12 py-10">
          <div className="font-mono text-xs text-volt tracking-[0.25em] uppercase mb-2">02 // VEHICLES</div>
          <h1 className="font-display font-black text-5xl tracking-tight">Fuhrpark</h1>
          <p className="text-white/60 mt-2 max-w-xl">Alle registrierten Fahrzeuge deines Chars — Standort, Zustand und Kennzeichen.</p>
          <div className="grid grid-cols-3 gap-4 mt-6 max-w-md">
            <div className="border border-tactical-border bg-black/60 backdrop-blur p-3">
              <div className="font-mono text-[10px] text-white/40 uppercase">Total</div>
              <div className="font-mono font-bold text-2xl" data-testid="veh-total">{items.length}</div>
            </div>
            <div className="border border-tactical-border bg-black/60 backdrop-blur p-3">
              <div className="font-mono text-[10px] text-white/40 uppercase">Geparkt</div>
              <div className="font-mono font-bold text-2xl text-tactical-success">{stored}</div>
            </div>
            <div className="border border-tactical-border bg-black/60 backdrop-blur p-3">
              <div className="font-mono text-[10px] text-white/40 uppercase">Ø Zustand</div>
              <div className="font-mono font-bold text-2xl text-volt">{avgCondition}%</div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-8 lg:p-12">
        <div className="flex gap-2 mb-6 flex-wrap">
          {cats.map(c => (
            <button
              key={c}
              data-testid={`veh-filter-${c}`}
              onClick={() => setFilter(c)}
              className={`font-mono text-xs uppercase tracking-widest px-4 py-2 border transition-colors duration-150 ${
                filter === c
                  ? "bg-white text-black border-white"
                  : "border-tactical-border text-white/60 hover:text-white hover:border-white/40"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 stagger">
          {filtered.map(v => {
            const Icon = catIcon(v.category);
            return (
              <div key={v.id} data-testid={`veh-card-${v.plate}`} className="border border-tactical-border bg-tactical-surface hover:border-white/40 transition-colors duration-150 group overflow-hidden">
                <div className={`relative h-44 overflow-hidden bg-gradient-to-br ${catAccent(v.category)}`}>
                  {vehicleImage(v.model) && (
                    <img
                      src={vehicleImage(v.model)}
                      alt={v.label}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover object-center opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-tactical-surface via-tactical-surface/40 to-transparent" />
                  <div className="absolute top-3 left-3 font-mono text-[10px] text-white bg-black/80 backdrop-blur border border-white/20 px-2 py-1 uppercase tracking-widest">{v.category}</div>
                  <div className="absolute top-3 right-3 w-9 h-9 border border-white/20 bg-black/80 backdrop-blur flex items-center justify-center">
                    <Icon size={16} className="text-white" />
                  </div>
                  <div className="absolute bottom-3 left-3 right-3">
                    <div className="font-display font-black text-2xl text-white leading-tight drop-shadow-lg">{v.label}</div>
                    <div className="font-mono text-[10px] text-white/70 mt-0.5">{v.model}</div>
                  </div>
                </div>
                <div className="p-5 space-y-3">
                  <div>
                    <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest mb-1">Kennzeichen</div>
                    <div className="font-mono font-bold text-lg bg-yellow-400 text-black inline-block px-3 py-1 tracking-widest">
                      {v.plate}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 font-mono text-xs">
                    <div>
                      <div className="text-white/40 uppercase tracking-wider mb-1">Standort</div>
                      <div className="text-white">{v.garage}</div>
                    </div>
                    <div>
                      <div className="text-white/40 uppercase tracking-wider mb-1">Status</div>
                      <div className={v.stored ? "text-tactical-success" : "text-tactical-warn"}>
                        ● {v.stored ? "Geparkt" : "Draußen"}
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between font-mono text-xs mb-1">
                      <span className="text-white/40 uppercase tracking-wider">Zustand</span>
                      <span className="text-white">{v.condition}%</span>
                    </div>
                    <Progress value={v.condition} className="h-1 bg-tactical-border" />
                  </div>

                  <div>
                    <div className="flex items-center justify-between font-mono text-xs mb-1">
                      <span className="text-white/40 uppercase tracking-wider flex items-center gap-1"><GasPump size={12} /> Tank</span>
                      <span className="text-white">{v.fuel}%</span>
                    </div>
                    <Progress value={v.fuel} className="h-1 bg-tactical-border" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-16 font-mono text-white/40 border border-tactical-border border-dashed">
            Keine Fahrzeuge in dieser Kategorie
          </div>
        )}
      </div>
    </div>
  );
}
